import React, { useEffect } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Form,
    Input,
    Row,
    Select,
    Space,
    Switch,
    Tag,
    Typography,
} from 'antd';
import {
    AppstoreOutlined,
    BgColorsOutlined,
    InfoCircleOutlined,
    LockOutlined,
    SafetyOutlined,
    TranslationOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useMessage } from '@/shared/hooks/useMessage';
import { Languages, PasswordChangeRequest, Themes } from '@/shared/api/model';
import { useChangePassword } from '@/shared/api/client/nub-user-resource';
import { useAuthStore } from '@/modules/auth';
import { useUserPreferencesStore } from '@/stores/user-preferences-store';
import { getDashboardPresetOptions } from '@/pages/home/dashboard-config';

const { Title, Text } = Typography;

const languageOptions = [
    { label: 'English', value: Languages.EN },
    { label: 'Swahili', value: Languages.SW },
];

const themeOptions = [
    { label: 'Light', value: Themes.LIGHT },
    { label: 'Dark', value: Themes.DARK },
];

const dashboardPresetOptions = getDashboardPresetOptions();

export const UserProfileView: React.FC = () => {
    const { dark } = useTheme();
    const message = useMessage();
    const user = useAuthStore((state) => state.user);
    const refreshUser = useAuthStore((state) => state.refreshUser);
    const language = useUserPreferencesStore((state) => state.language);
    const theme = useUserPreferencesStore((state) => state.theme);
    const menuMode = useUserPreferencesStore((state) => state.menuMode);
    const dashboardPreset = useUserPreferencesStore((state) => state.dashboardPreset);
    const setLanguage = useUserPreferencesStore((state) => state.setLanguage);
    const setTheme = useUserPreferencesStore((state) => state.setTheme);
    const setMenuMode = useUserPreferencesStore((state) => state.setMenuMode);
    const setDashboardPreset = useUserPreferencesStore((state) => state.setDashboardPreset);
    const [preferencesForm] = Form.useForm();
    const [passwordForm] = Form.useForm<PasswordChangeRequest & { confirmPassword: string }>();
    const changePasswordMutation = useChangePassword();

    useEffect(() => {
        preferencesForm.setFieldsValue({
            language,
            theme,
            topMenu: menuMode === 'top',
            dashboardPreset,
        });
    }, [dashboardPreset, language, menuMode, preferencesForm, theme]);

    const handleSavePreferences = (values: { language: Languages; theme: Themes; topMenu: boolean; dashboardPreset: string }) => {
        setLanguage(values.language);
        setTheme(values.theme);
        setMenuMode(values.topMenu ? 'top' : 'side');
        setDashboardPreset(values.dashboardPreset as any);
        message.success('Profile preferences updated');
    };

    const handlePasswordChange = async (values: PasswordChangeRequest & { confirmPassword: string }) => {
        if (!user?.userId) {
            message.error('Unable to identify the current user');
            return;
        }

        try {
            await changePasswordMutation.mutateAsync({
                userId: user.userId,
                data: {
                    currentPassword: values.currentPassword,
                    newPassword: values.newPassword,
                },
            });
            await refreshUser();
            passwordForm.resetFields();
            message.success('Password changed successfully');
        } catch (error: any) {
            message.error(error?.response?.data?.message || error?.message || 'Failed to change password');
        }
    };

    return (
        <div className={`p-6 ${dark ? 'bg-gray-900' : 'bg-gray-50'} min-h-screen`}>
            <div className="max-w-7xl mx-auto">
                <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <UserOutlined className="text-3xl text-blue-500" />
                        <div>
                            <Title level={2} style={{ margin: 0, color: dark ? '#ffffff' : undefined }}>
                                Profile & Preferences
                            </Title>
                            <Text style={{ color: dark ? '#9ca3af' : undefined }}>
                                Manage your account details, security, language, theme, and navigation preferences.
                            </Text>
                        </div>
                    </div>
                    <Space wrap>
                        {user?.changePassword && <Tag color="red">Password update required</Tag>}
                        <Tag color={theme === Themes.DARK ? 'purple' : 'gold'}>{theme} mode</Tag>
                        <Tag color="blue">{menuMode === 'top' ? 'Top menu' : 'Side menu'}</Tag>
                    </Space>
                </div>

                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={10}>
                        <Card title={<Space><UserOutlined />Account overview</Space>}>
                            <Descriptions column={1} size="small" bordered>
                                <Descriptions.Item label="Full name">{user?.fullname || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Username">{user?.username || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Email">{user?.email || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Phone">{user?.phoneNo || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Role">{user?.role || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Branch">{user?.branchName || user?.branch || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Session date">{user?.sessionDate || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Password status">
                                    {user?.passwordChanged ? <Tag color="green">Changed</Tag> : <Tag color="orange">Not changed</Tag>}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    <Col xs={24} xl={14}>
                        <Card title={<Space><InfoCircleOutlined />Profile notes</Space>}>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Swahili support is now selectable"
                                    description="The language option is available in profile settings. Full translation coverage is still in progress and will be expanded gradually."
                                />
                                <Alert
                                    type="success"
                                    showIcon
                                    message="Navigation preference moved to profile"
                                    description="The top menu flag is now controlled from your profile settings instead of the top bar, so all personal preferences live in one place."
                                />
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Card title={<Space><AppstoreOutlined />Display & navigation</Space>}>
                            <Form
                                form={preferencesForm}
                                layout="vertical"
                                onFinish={handleSavePreferences}
                            >
                                <Form.Item
                                    label={<Space><TranslationOutlined />Language</Space>}
                                    name="language"
                                    rules={[{ required: true, message: 'Please select a language' }]}
                                >
                                    <Select options={languageOptions} />
                                </Form.Item>

                                <Form.Item
                                    label={<Space><BgColorsOutlined />Theme</Space>}
                                    name="theme"
                                    rules={[{ required: true, message: 'Please select a theme' }]}
                                >
                                    <Select options={themeOptions} />
                                </Form.Item>

                                <Form.Item
                                    label={<Space><AppstoreOutlined />Homepage dashboard</Space>}
                                    name="dashboardPreset"
                                    rules={[{ required: true, message: 'Please select a dashboard preset' }]}
                                >
                                    <Select options={dashboardPresetOptions} />
                                </Form.Item>

                                <Form.Item
                                    label="Top navigation menu"
                                    name="topMenu"
                                    valuePropName="checked"
                                >
                                    <Switch checkedChildren="Top" unCheckedChildren="Side" />
                                </Form.Item>

                                <div className="mb-4">
                                    <Text type="secondary">
                                        Theme, menu mode, homepage dashboard, and language are applied from your profile preferences immediately after saving.
                                    </Text>
                                </div>

                                <Button type="primary" htmlType="submit">
                                    Save preferences
                                </Button>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Card title={<Space><SafetyOutlined />Security</Space>}>
                            <Form
                                form={passwordForm}
                                layout="vertical"
                                onFinish={handlePasswordChange}
                            >
                                <Form.Item
                                    label="Current password"
                                    name="currentPassword"
                                    rules={[{ required: true, message: 'Please enter your current password' }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>

                                <Form.Item
                                    label="New password"
                                    name="newPassword"
                                    rules={[
                                        { required: true, message: 'Please enter a new password' },
                                        { min: 8, message: 'Password must be at least 8 characters' },
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>

                                <Form.Item
                                    label="Confirm new password"
                                    name="confirmPassword"
                                    dependencies={['newPassword']}
                                    rules={[
                                        { required: true, message: 'Please confirm the new password' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('newPassword') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Passwords do not match'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>

                                <div className="mb-4">
                                    <Text type="secondary">
                                        Use a strong password that you do not reuse on other systems.
                                    </Text>
                                </div>

                                <Button type="primary" htmlType="submit" loading={changePasswordMutation.isPending}>
                                    Change password
                                </Button>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};
